import { useState } from "react";
import axios from "axios";
import {
  Home,
  Sprout,
  Bird,
  Store,
  Car,
  Trees,
  CircleHelp,
  Check,
  Loader2,
  Send,
  AlertCircle,
  ArrowRight,
  Flame,
  ShieldAlert,
} from "lucide-react";
import { WHATSAPP_LINK } from "../constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAMAGE_TYPES = [
  { id: "abitazione", label: "Abitazione", icon: Home },
  { id: "terreno_agricolo", label: "Terreno agricolo", icon: Sprout },
  { id: "animali_allevamento", label: "Animali / allevamento", icon: Bird },
  { id: "attivita_commerciale", label: "Attività commerciale", icon: Store },
  { id: "veicoli_mezzi", label: "Veicoli / mezzi", icon: Car },
  { id: "boschi_uliveto", label: "Bosco / uliveto", icon: Trees },
  { id: "altro", label: "Altro", icon: CircleHelp },
];

const URGENCIES = [
  { id: "bassa", label: "Bassa", desc: "Situazione gestita" },
  { id: "media", label: "Media", desc: "Da seguire a breve" },
  { id: "alta", label: "Alta", desc: "Ho bisogno subito" },
];

export const FireDamage = () => {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    damage_types: [],
    description: "",
    location: "",
    urgency: "media",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const handleField = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleType = (id) => {
    setForm((prev) => {
      const has = prev.damage_types.includes(id);
      return {
        ...prev,
        damage_types: has
          ? prev.damage_types.filter((t) => t !== id)
          : [...prev.damage_types, id],
      };
    });
  };

  const reset = () => {
    setForm({
      name: "",
      contact: "",
      damage_types: [],
      description: "",
      location: "",
      urgency: "media",
    });
    setStatus("idle");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.damage_types.length === 0) {
      setError("Seleziona almeno un tipo di danno.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/fire-damage`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Qualcosa è andato storto. Riprova o scrivici su WhatsApp."
      );
    }
  };

  return (
    <section
      id="danni"
      data-testid="fire-damage-section"
      className="relative bg-gradient-to-br from-[#FBF9F6] via-[#FBF9F6] to-[#f5ebe0] py-24 sm:py-32 overflow-hidden border-t border-[#E5E1D8]"
    >
      {/* Decorative */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.7]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 85% 15%, #C15C3D15 0%, transparent 40%), radial-gradient(circle at 15% 85%, #2E4F3B10 0%, transparent 50%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header — versione migliorata */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#C15C3D]/12 border border-[#C15C3D]/30 px-4 py-2 mb-6">
            <Flame className="w-4 h-4 text-[#C15C3D]" />
            <span className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#C15C3D]">
              Supporto post-incendio · Riservato
            </span>
          </div>
          <h2
            data-testid="fire-damage-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#1A1A1A] leading-[0.95]"
          >
            Hai subito dei danni
            <br />
            <span className="italic font-light text-[#C15C3D]">
              dall'incendio?
            </span>
          </h2>
          <p className="mt-7 text-lg sm:text-xl text-[#5C5C5C] leading-relaxed">
            Raccontaci cosa è successo. Ogni segnalazione ci permette di
            mappare i bisogni reali e indirizzare il supporto — economico,
            logistico, umano — a chi ne ha davvero bisogno.
            <span className="block mt-3 text-[#1A1A1A] font-semibold">
              Non sei solo.
            </span>
          </p>

          {/* Reassurance pills */}
          <div className="mt-7 flex flex-wrap gap-2">
            {[
              "Dati protetti",
              "Risposta entro 48h",
              "Nessun costo",
            ].map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2E4F3B] bg-white border border-[#E5E1D8] rounded-full px-3 py-1.5"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#2E4F3B]" />
                {p}
              </span>
            ))}
          </div>
        </div>

        {status === "success" ? (
          <div
            data-testid="fire-damage-success"
            className="rounded-3xl bg-[#2E4F3B] text-white p-10 sm:p-16 text-center max-w-3xl mx-auto"
          >
            <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#E8C9A0]" />
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-black leading-tight">
              Ti abbiamo sentito.
            </h3>
            <p className="mt-4 text-white/80 max-w-xl mx-auto leading-relaxed">
              La tua segnalazione è arrivata. Ti contatteremo per capire
              insieme come muoverci. All'evento sui Condotti avremo anche uno
              spazio dal vivo per chi vuole raccontarci di persona.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/segnalazioni"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="fire-damage-success-whatsapp-btn"
                className="wa-pulse inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-bold px-7 py-4 hover:bg-[#1fb855] transition-all"
              >
                Le tue segnalazioni
                <ArrowRight className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={reset}
                data-testid="fire-damage-new-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 text-white font-semibold px-7 py-4 hover:bg-white/20 border border-white/20 transition-all"
              >
                Nuova segnalazione
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-testid="fire-damage-form"
            className="space-y-10"
          >
            {/* Step 1 — Damage types */}
            <fieldset>
              <legend className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C15C3D] mb-5 flex items-center gap-2">
                <ShieldAlert className="w-3.5 h-3.5" />
                1. Cosa hai subito?{" "}
                <span className="text-[#5C5C5C] normal-case tracking-normal text-[11px] font-medium ml-1">
                  (anche più di uno)
                </span>
              </legend>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {DAMAGE_TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = form.damage_types.includes(t.id);
                  return (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => {
                        toggleType(t.id);
                        if (status === "error") setStatus("idle");
                      }}
                      data-testid={`fire-damage-type-${t.id}`}
                      className={`text-left rounded-2xl p-5 border transition-all ${
                        active
                          ? "bg-[#C15C3D] text-white border-[#C15C3D] shadow-md"
                          : "bg-white border-[#E5E1D8] hover:border-[#C15C3D]"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0 ${
                            active
                              ? "bg-white/20 text-white"
                              : "bg-[#FBF9F6] text-[#C15C3D] border border-[#E5E1D8]"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                        </span>
                        <div
                          className={`font-display font-bold text-sm sm:text-base ${
                            active ? "text-white" : "text-[#1A1A1A]"
                          }`}
                        >
                          {t.label}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Step 2 — Details */}
            <fieldset>
              <legend className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C15C3D] mb-5">
                2. Racconta cosa è successo
              </legend>
              <div className="rounded-3xl bg-white border border-[#E5E1D8] p-6 sm:p-8 space-y-5">
                <div>
                  <label
                    htmlFor="fd-location"
                    className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2"
                  >
                    Dove
                  </label>
                  <input
                    id="fd-location"
                    name="location"
                    type="text"
                    required
                    minLength={2}
                    value={form.location}
                    onChange={handleField}
                    data-testid="fire-damage-input-location"
                    placeholder="Località, via, zona (es. Loc. Mucigliani, Asciano)"
                    className="w-full bg-[#FBF9F6] border border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A9A] focus:outline-none focus:border-[#C15C3D] focus:ring-2 focus:ring-[#C15C3D]/10 transition-all"
                  />
                </div>
                <div>
                  <label
                    htmlFor="fd-description"
                    className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2"
                  >
                    Descrizione
                  </label>
                  <textarea
                    id="fd-description"
                    name="description"
                    required
                    minLength={5}
                    rows={5}
                    value={form.description}
                    onChange={handleField}
                    data-testid="fire-damage-input-description"
                    placeholder="Cosa hai perso o danneggiato. Quando. Come stai ora. Se hai foto le potrai condividere dopo su WhatsApp."
                    className="w-full bg-[#FBF9F6] border border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A9A] focus:outline-none focus:border-[#C15C3D] focus:ring-2 focus:ring-[#C15C3D]/10 transition-all resize-none"
                  />
                </div>
              </div>
            </fieldset>

            {/* Step 3 — Urgency */}
            <fieldset>
              <legend className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C15C3D] mb-5">
                3. Quanto è urgente?
              </legend>
              <div className="grid sm:grid-cols-3 gap-3">
                {URGENCIES.map((u) => {
                  const active = form.urgency === u.id;
                  return (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => setForm({ ...form, urgency: u.id })}
                      data-testid={`fire-damage-urgency-${u.id}`}
                      className={`text-left rounded-2xl p-5 border transition-all ${
                        active
                          ? u.id === "alta"
                            ? "bg-[#C15C3D] text-white border-[#C15C3D] shadow-md"
                            : "bg-[#2E4F3B] text-white border-[#2E4F3B] shadow-md"
                          : "bg-white border-[#E5E1D8] hover:border-[#C15C3D]"
                      }`}
                    >
                      <div
                        className={`font-display font-bold text-base ${
                          active ? "text-white" : "text-[#1A1A1A]"
                        }`}
                      >
                        {u.label}
                      </div>
                      <div
                        className={`text-xs mt-1 leading-tight ${
                          active ? "text-white/85" : "text-[#5C5C5C]"
                        }`}
                      >
                        {u.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Step 4 — Contact */}
            <fieldset>
              <legend className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C15C3D] mb-5">
                4. Come ti possiamo contattare
              </legend>
              <div className="rounded-3xl bg-white border border-[#E5E1D8] p-6 sm:p-8 grid md:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="fd-name"
                    className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2"
                  >
                    Nome e cognome
                  </label>
                  <input
                    id="fd-name"
                    name="name"
                    type="text"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={handleField}
                    data-testid="fire-damage-input-name"
                    placeholder="Come ti chiami?"
                    className="w-full bg-[#FBF9F6] border border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A9A] focus:outline-none focus:border-[#C15C3D] focus:ring-2 focus:ring-[#C15C3D]/10 transition-all"
                  />
                </div>
                <div>
                  <label
                    htmlFor="fd-contact"
                    className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2"
                  >
                    Telefono o email
                  </label>
                  <input
                    id="fd-contact"
                    name="contact"
                    type="text"
                    required
                    minLength={3}
                    value={form.contact}
                    onChange={handleField}
                    data-testid="fire-damage-input-contact"
                    placeholder="Come vuoi essere ricontattato"
                    className="w-full bg-[#FBF9F6] border border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A9A] focus:outline-none focus:border-[#C15C3D] focus:ring-2 focus:ring-[#C15C3D]/10 transition-all"
                  />
                </div>
              </div>
            </fieldset>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="fire-damage-submit-btn"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-[#C15C3D] text-white font-bold px-8 py-4 hover:bg-[#a94b2f] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Invio in corso…
                  </>
                ) : (
                  <>
                    Invia la segnalazione
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="text-xs text-[#5C5C5C] leading-relaxed max-w-md">
                I dati sono usati solo per aiutarti e coordinare il supporto.
                Nessuna diffusione pubblica senza il tuo consenso.
              </p>
            </div>

            {status === "error" && (
              <div
                data-testid="fire-damage-error"
                className="flex items-start gap-3 bg-[#C15C3D]/10 border border-[#C15C3D]/30 rounded-xl p-4 max-w-2xl"
              >
                <AlertCircle className="w-5 h-5 text-[#C15C3D] shrink-0 mt-0.5" />
                <div className="text-sm text-[#1A1A1A]">{error}</div>
              </div>
            )}
          </form>
        )}
      </div>
    </section>
  );
};
